/** @jsxImportSource react */

import { normJsonApi } from '@common/helpers/jsonApi';
import { cn } from '@common/lib/clsxm';
import EntityTile from '@react/card';
import { getEventPropsForOOB } from '@react/gwizzy/components/OutOfBudgetWizard/helpers';
import OutOfBudgetWizard from '@react/gwizzy/components/OutOfBudgetWizard/OutOfBudgetWizard';
import { useQuery } from '@react/services/api';
import Spinner from '@react/shared/components/Spinner';
import { useToast } from '@react/shared/components/Toast';
import { Button, Link } from '@react/system';
import { SearchResourceNew } from '@sly/core/src/types';
import { Checkmark, Edit } from '@sly/icons/react';
import { useState } from 'react';

const careLevelToTocMap = {
  'memory-care': 'memory-care',
  'light-care': 'independent-living',
  'heavy-care': 'assisted-living',
  'no-care': 'independent-living',
};

export default function OutOfBudgetWidget({
  initialOptions,
  isLocalStorageEnabled = true,
  onSubmitCallback,
  className,
  eventProps = {},
}: {
  initialOptions: Record<string, any>;
  isLocalStorageEnabled?: boolean;
  onSubmitCallback?: (values: Record<string, any>) => void;
  className?: string;
  eventProps?: Record<string, any>;
}) {
  const [screen, setScreen] = useState(
    initialOptions?.initialScreen || 'intro'
  );

  const { pushError } = useToast();

  const [searchPageData, setSearchPageData] = useState<
    SearchResourceNew[] | null
  >(null);

  const [searchPageURL, setSearchPageURL] = useState('');

  const getSearchPage = useQuery('getSearchPage');

  const handleSubmit = async (values: Record<string, any>) => {
    setScreen('loading');
    const searchParams = values?.location?.searchParams || {};
    const adl: keyof typeof careLevelToTocMap = values?.adl || '';
    const budgetRange = values?.budgetRange || '';
    const { state, city } = searchParams;

    const toc = careLevelToTocMap?.[adl] || 'assisted-living';

    const filters = {
      include: 'community',
      'filter[starting-rate]': budgetRange,
      'filter[toc]': toc,
      'filter[state]': state,
      'filter[city]': city,
    };

    try {
      const response = await getSearchPage(filters);
      const normalized = normJsonApi(response);

      setSearchPageData(normalized);
      setScreen('communities');
    } catch (error) {
      console.error('Error fetching search page data', error);
      pushError('Oops! Something went wrong. Please try again.');
      setScreen('wizard');
    }

    const url = `/${toc}/${state}/${city}?starting-rate=${budgetRange}`;
    setSearchPageURL(url);
    if (onSubmitCallback) {
      onSubmitCallback(values);
    }
  };

  const reasons = [
    'Get a free list of personalized recommendations',
    'We’ll help you compare options and choose the right community for your needs and budget',
    'Our advice is free! We’re paid by the senior living communities in our network',
  ];

  return (
    <div className={cn('rounded shadow', className)}>
      {screen === 'intro' && (
        <div>
          <div className='space-y-2 p-6'>
            <h3 className='font-t-m pb-4 text-center'>
              Answer a few questions to personalize your recommendations
            </h3>
            <Button
              className='w-full'
              onClick={() => setScreen('wizard')}
              eventProps={getEventPropsForOOB(eventProps)}
            >
              Get started
            </Button>
          </div>
          <div className='p-6'>
            <ul className='space-y-4'>
              {reasons.map((reason, index) => (
                <li className='flex items-center gap-2' key={index}>
                  <Checkmark />
                  <div>{reason}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {screen === 'wizard' && (
        <div className='px-12'>
          <OutOfBudgetWizard
            initialOptions={initialOptions}
            onSubmit={handleSubmit}
            isLocalStorageEnabled={isLocalStorageEnabled}
            eventProps={eventProps}
          />
        </div>
      )}

      {screen === 'loading' && (
        <div className='flex h-full min-h-[300px] max-w-full flex-col items-center justify-center gap-3 rounded-md bg-white px-6 pb-6'>
          <Spinner />
          <div>Curating personalized recommendations</div>
        </div>
      )}

      {screen === 'communities' && searchPageData && (
        <div className='p-6'>
          {searchPageData.length > 0 ? (
            <div className='space-y-4'>
              <div className='flex flex-col justify-between md:flex-row md:items-center'>
                <h3 className='font-t-m'>
                  We recommend these {searchPageData.length} communities
                </h3>
                <Button
                  variant='searchFilter'
                  className='font-b-s min-w-max self-end !py-2 !px-4'
                  onClick={() => setScreen('wizard')}
                  eventProps={getEventPropsForOOB({
                    text: 'Edit preference',
                    ...eventProps,
                  })}
                >
                  <Edit size='s' /> Edit preference
                </Button>
              </div>
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2 [&>a]:hover:no-underline'>
                {searchPageData
                  .filter((_, index) => index < 4)
                  .map((community) => (
                    <EntityTile
                      key={community.id}
                      entity={community}
                      intent='vertical'
                      eventProps={getEventPropsForOOB(eventProps)}
                    />
                  ))}
              </div>
              {searchPageURL && searchPageData.length > 3 && (
                <div>
                  <Link
                    to={searchPageURL}
                    underlined
                    eventProps={{
                      location: 'outOfBudgetWizard',
                      ...eventProps,
                      text: 'See all',
                    }}
                  >
                    See all {searchPageData.length} options
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <div className='space-y-6 text-center'>
              <h3 className='font-t-m'>
                We couldn&apos;t find any communities that match your criteria
              </h3>
              <p>
                Try adjusting your budget, location, or care needs to see more
                options
              </p>
              <Button
                className='w-full'
                onClick={() => setScreen('wizard')}
                eventProps={getEventPropsForOOB(eventProps)}
              >
                Try again
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
